import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import type { ProductCategory } from '@/api/admin-products-types'
import { useCreateProductCategory } from '@/api/api-mutations'
import { useListProductCategories } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { useCreatedToast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, FormLabel, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { getHierarchy } from '@/utils/hierarchy-utils'
import { useDebounce } from '@/utils/hooks'

export const Route = createFileRoute('/_authenticated/_general/product-categories/create')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.ProductCategory.CreateTitle()
	}
})

function RouteComponent() {
	return (
		<RouteModal asDrawer>
			<NewCategoryForm />
		</RouteModal>
	)
}

type CategoryTreeItem = ProductCategory & {
	level: number
	children: CategoryTreeItem[]
}

function NewCategoryForm() {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const createdToast = useCreatedToast()

	const createCategory = useCreateProductCategory()

	const form = useAppForm({
		defaultValues: {
			name: '',
			parentCategoryUuid: undefined as string | undefined
		},
		onSubmit: async ({ value }) => {
			const result = await createCategory({
				body: {
					name: value.name,
					parentCategoryUuid: value.parentCategoryUuid
				}
			})
			createdToast(L.ProductCategory.CreateSuccessToast({ name: value.name }), {
				to: '/product-categories/$categoryUuid',
				params: { categoryUuid: result.response.uuid }
			})
			handleSuccess()
		}
	})

	const [categorySearch, setCategorySearch] = useState('')
	const debouncedCategorySearch = useDebounce(categorySearch)
	const categories = useListProductCategories({
		q: debouncedCategorySearch,
		limit: 50
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.ProductCategory.CreateTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							children={field => <field.TextField autoFocus label={L.Fields.Name()} />}
						/>
						<form.AppField
							name='parentCategoryUuid'
							children={field => (
								<field.ComboboxField
									label={L.ProductCategory.ParentCategory()}
									multiple={false}
									optionsLoading={categories.isLoading}
									onSearchValueChange={setCategorySearch}
									options={
										categories.data?.categories?.map(c => ({ label: c.name, value: c.uuid })) ?? []
									}
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>

			{/* {showParentSelector && (
				<FocusModal onClose={() => setShowParentSelector(false)}>
					<div className='flex flex-col gap-6 p-6'>
						<div className='text-center'>
							<Heading>{L.Categories.Create.SelectParentTitle()}</Heading>
							<p className='mt-2 text-sm text-muted-foreground'>
								{L.Categories.Create.SelectParentDescription()}
							</p>
						</div>

						<div className='max-h-96 overflow-y-auto space-y-1'>
							<Button
								type='button'
								variant='transparent'
								onClick={() => {
									form.setFieldValue('parentCategoryUuid', undefined)
									setShowParentSelector(false)
								}}
								className='w-full justify-start'
							>
								{L.Categories.Create.NoParent()}
							</Button>
							{categoryTree.map(category => (
								<Button
									key={category.uuid}
									type='button'
									variant='transparent'
									onClick={() => {
										form.setFieldValue('parentCategoryUuid', category.uuid)
										setShowParentSelector(false)
									}}
									className='w-full justify-start'
									style={{ paddingLeft: `${16 + category.level * 24}px` }}
								>
									{category.name}
								</Button>
							))}
						</div>

						<div className='flex justify-end'>
							<Button
								type='button'
								
								onClick={() => setShowParentSelector(false)}
							>
								{L.General.Cancel()}
							</Button>
						</div>
					</div>
				</FocusModal>
			)} */}
		</form.AppForm>
	)
}
